// What a recipe is for: breakfast, a dessert, a drink. The same few words on the server and in the app, in the order a
// cookbook's sections follow (a meal's day, then what goes around it). A recipe can be more than one ("breakfast" and
// "baking"); in a cookbook it sits in the section of the first. Stored on the recipe as its keys joined by commas.

export const MEALS = [
  'breakfast',
  'lunch',
  'dinner',
  'appetizer',
  'soup',
  'side',
  'baking',
  'dessert',
  'drink',
  'snack',
] as const
export type Meal = (typeof MEALS)[number]

// The word on a recipe, and the name of its section in a cookbook.
export const MEAL_NAMES: Record<Meal, { one: string; section: string }> = {
  breakfast: { one: 'Breakfast', section: 'Breakfast' },
  lunch: { one: 'Lunch', section: 'Lunch' },
  dinner: { one: 'Dinner', section: 'Dinner' },
  appetizer: { one: 'Appetizer', section: 'Appetizers' },
  soup: { one: 'Soup & salad', section: 'Soups & salads' },
  side: { one: 'Side', section: 'Sides' },
  baking: { one: 'Bread & baking', section: 'Bread & baking' },
  dessert: { one: 'Dessert', section: 'Desserts' },
  drink: { one: 'Drink', section: 'Drinks' },
  snack: { one: 'Snack', section: 'Snacks' },
}
// The section for recipes not given a meal, last in the book.
export const OTHER_SECTION = 'More recipes'

export const isMeal = (value: unknown): value is Meal => MEALS.includes(value as Meal)

// Known meals only, each once, in the list's order.
export const tidyMeals = (meals: readonly string[]): Meal[] => MEALS.filter((m) => meals.includes(m))

export const storeMeals = (meals: readonly string[]) => tidyMeals(meals).join(',')
export const readMeals = (stored: string | null | undefined) => tidyMeals((stored ?? '').split(','))

// The section a recipe goes in: its first meal, or none.
export const sectionOf = (meals: readonly string[]): Meal | null => tidyMeals(meals)[0] ?? null
const rank = (meals: readonly string[]) => {
  const meal = sectionOf(meals)
  return meal ? MEALS.indexOf(meal) : MEALS.length
}

// Recipes put into their sections, keeping their order within each.
export const bySection = <T>(items: readonly T[], mealsOf: (item: T) => readonly string[]) =>
  items
    .map((item, i) => ({ item, i, rank: rank(mealsOf(item)) }))
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map((x) => x.item)

export const sectionName = (meal: Meal | null) => (meal ? MEAL_NAMES[meal].section : OTHER_SECTION)
